import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Building2, ArrowRight, ImageOff, ShieldCheck } from "lucide-react";
import { priceRange, STATUS_LABEL } from "@/lib/constants";

function ImageLabel({ type }) {
  if (type === "ACTUAL_PHOTO") return <span className="bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-sm">Actual Image</span>;
  if (type === "ARTISTIC_IMPRESSION") return <span className="bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-sm">Artist's Impression</span>;
  return null;
}

export default function ProjectCard({ project, index = 0 }) {
  const [imgError, setImgError] = React.useState(false);
  const p = project;
  const hasImage = !!p.primary_image && !imgError;
  const status = STATUS_LABEL[p.status] || (p.status ? p.status : "On Enquiry");

  return (
    <article
      data-testid={`project-card-${p.slug}`}
      className="group flex flex-col rounded-md border border-border bg-white overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="relative h-56 overflow-hidden bg-muted">
        <Link to={`/projects/${p.slug}`} aria-label={p.name}>
          {hasImage ? (
            <img src={p.primary_image} alt={`${p.name}, ${p.location_name}`} loading="lazy" referrerPolicy="no-referrer" onError={() => setImgError(true)}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <div className="h-full w-full flex flex-col items-center justify-center text-center px-4 bg-gradient-to-br from-muted to-secondary">
              <ImageOff className="h-7 w-7 text-primary/50 mb-2" aria-hidden />
              <p className="text-xs font-medium text-primary/70">Official image pending approval</p>
              <p className="text-[10px] text-muted-foreground mt-1">{p.developer_name}</p>
            </div>
          )}
        </Link>
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          <span className="bg-primary/90 text-primary-foreground text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm">{p.region_label || status}</span>
          {p.featured && <span className="bg-accent text-accent-foreground text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm">Featured</span>}
        </div>
        {hasImage && <div className="absolute bottom-3 left-3"><ImageLabel type={p.image_source_type} /></div>}
        {p.source_verified && (
          <span className="absolute bottom-3 right-3 flex items-center gap-1 bg-green-600 text-white text-[10px] px-2 py-0.5 rounded-sm"><ShieldCheck className="h-3 w-3" aria-hidden /> Verified</span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs uppercase tracking-[0.14em] text-accent font-semibold mb-1">{p.developer_name}</p>
        <Link to={`/projects/${p.slug}`}><h3 className="font-display text-lg text-foreground leading-snug hover:text-primary transition-colors">{p.name}</h3></Link>
        <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" aria-hidden /> {p.location_name}, Pune</p>

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Configuration</p>
            <p className="text-foreground font-medium">{p.configurations?.length ? p.configurations.join(", ") : "On request"}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</p>
            <p className="text-foreground font-medium">{status}</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-border flex items-end justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Price</p>
            <p className="font-display text-base text-primary">{priceRange(p)}</p>
          </div>
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Building2 className="h-3.5 w-3.5" aria-hidden /> {p.region_label}</span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link to={`/projects/${p.slug}`} data-testid={`view-${p.slug}`}
            className="text-center text-sm font-semibold py-2.5 rounded-sm border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">View Details</Link>
          <Link to={`/projects/${p.slug}#enquire`} data-testid={`enquire-${p.slug}`}
            className="text-center text-sm font-semibold py-2.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">Enquire Now</Link>
        </div>
      </div>
    </article>
  );
}
